import { useMemo } from "react";
import { useDataMap } from "../../../../common/hooks/api/useDataMap";
import { useOrgDataSources } from "../../../hooks/useOrganizationVendors";
import { isSourcesPageComplete } from "./DataFlowsSourcesPage";
import { CollectionGroupDetailsDto } from "../../../../common/service/server/dto/CollectionGroupDto";
import { OrganizationPublicId } from "../../../../common/service/server/Types";
import { SOURCE_KEY_CONSUMER_DIRECTLY } from "../../../types/Vendor";
import { isEmptyDataMap } from "../../../../common/service/resources/data-map/dataMapHelpers";
import { useOnboardingProgress } from "../../../../common/hooks/useOnboardingProgress";
import { useSurvey } from "../../../../common/hooks/api/useSurvey";
import { usePrimaryOrganization, usePrimaryOrganizationId } from "../../../hooks";

// Mirrors EmployeePageWrapper's gate; string-inlined to avoid importing page modules here.
const DEVELOPER_INSTRUCTIONS_SURVEY_NAME = "developer-instruction-survey";
const FINISHED_HR_DATA_MAP_SLUG = "finished-hr-data-map";

/**
 * Whether a Data Flows child page should show its onboarding ProgressFooter.
 * Live for the whole data-map onboarding stretch (DES-30) — previously each page
 * snapshotted "am I complete" once on load, so arriving at a completed page offered
 * no forward path. Consumer reads the backend onboarding progress; HR reads the same
 * finished-hr-data-map survey answer EmployeePageWrapper uses.
 *
 * `isPageComplete` keeps the footer available on a step that still has work left while the org is
 * still onboarding — e.g. a collection group added mid-flow, whose Step 1 is empty.
 *
 * Once pol-7563's `finishedOnboardingLatch` is set the org is out of onboarding entirely and no
 * footer shows, matching how the other onboarding panels suppress themselves. The HR flow keeps
 * its own signal: the org-level latch says nothing about whether the HR data map is done.
 */
export function useDataFlowsOnboardingFooterGate(isHr: boolean, isPageComplete = true): boolean {
  const orgId = usePrimaryOrganizationId();
  const [onboardingProgress] = useOnboardingProgress(orgId);
  const [primaryOrg] = usePrimaryOrganization();
  const { answers: devInstructionsSurveyAnswers } = useSurvey(
    orgId,
    DEVELOPER_INSTRUCTIONS_SURVEY_NAME,
  );

  if (isHr) {
    if (!isPageComplete) {
      return true;
    }
    return devInstructionsSurveyAnswers?.[FINISHED_HR_DATA_MAP_SLUG] !== "true";
  }
  if (primaryOrg?.finishedOnboardingLatch === true) {
    return false;
  }
  if (!isPageComplete) {
    return true;
  }
  return (
    onboardingProgress != null && !onboardingProgress.dataMapOnboardingProgress.isDataFlowsComplete
  );
}

/**
 * Sources completeness for the consumer Data Flows dashboard (the backend gates
 * isDataFlowsComplete on Sources, so the dashboard must be able to narrate it).
 * Mirrors the Sources page's own footer computation, across every non-employment
 * collection group on the page.
 */
export function useDataFlowsSourcesCompletion(
  orgId: OrganizationPublicId | undefined,
  groups: CollectionGroupDetailsDto[] | undefined,
) {
  const { data: dataMap, request: dataMapRequest } = useDataMap(orgId);
  const [orgDataSources, orgDataSourcesRequest] = useOrgDataSources(orgId);

  return useMemo(() => {
    const groupIds = (groups ?? []).map((group) => group.id);
    // useDataMap hands back an empty map while the query is unresolved, so truthiness is not a
    // readiness signal — gate on the requests, or the caller renders a false "incomplete" dot.
    const isReady =
      dataMapRequest.success &&
      orgDataSourcesRequest.success &&
      Boolean(groups) &&
      Boolean(dataMap);

    if (!isReady || !dataMap || isEmptyDataMap(dataMap) || groupIds.length === 0) {
      return { isReady, isSourcesComplete: false };
    }

    const [isSourcesComplete] = isSourcesPageComplete(
      orgDataSources,
      dataMap,
      groupIds,
      "CONSUMER",
      { [SOURCE_KEY_CONSUMER_DIRECTLY]: "Consumer" },
    );

    return { isReady, isSourcesComplete };
  }, [dataMap, dataMapRequest.success, orgDataSources, orgDataSourcesRequest.success, groups]);
}
